import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icons';
import { LifecycleBand, SceneNegotiation, SceneAudit, SceneVersions, SceneHashChain } from '../components/LandingScenes';
import { fetchPlans } from '../data/billing';
import './Landing.css';

// Public marketing landing — what signed-out visitors see at fast.legal.
// Also serves dual-host mode (REACT_APP_MARKETING_HOST): there the CTAs point
// at the app subdomain instead of the local /login route.
const MARKETING_HOST = process.env.REACT_APP_MARKETING_HOST || '';
const ON_MARKETING_HOST = MARKETING_HOST && window.location.hostname === MARKETING_HOST;
const APP_BASE = ON_MARKETING_HOST ? `https://app.${MARKETING_HOST}` : '';
const SIGNIN_URL = `${APP_BASE}/login`;
const SIGNUP_URL = `${APP_BASE}/login?signup=1`;

// CTA that works on both hosts: router Link locally, plain anchor cross-host.
function Cta({ to, className, children }) {
  if (ON_MARKETING_HOST) return <a className={className} href={to}>{children}</a>;
  return <Link className={className} to={to}>{children}</Link>;
}

// Hero background film (media/homepage-film). Poster paints first; the clip
// attaches after first paint so it never competes for LCP. Reduced motion
// and Save-Data stay on the poster; narrow viewports get the 720p cut.
const HERO_VIDEO_BASE = (process.env.REACT_APP_HERO_VIDEO_BASE || '/media/homepage-film').replace(/\/$/, '');
// Bump on every re-cut: /media/** is cached for a week, and the file names
// never change, so this query string is what actually busts the cache.
const HERO_FILM_VERSION = '2026-10-07d';
const film = (name) => `${HERO_VIDEO_BASE}/${name}?v=${HERO_FILM_VERSION}`;

// The desktop cut carries a pre-rendered voiceover (media/homepage-film/
// narration). Browsers block unmuted autoplay, so the loop is silent until
// the visitor taps "Play with sound": the film restarts from 0 unmuted with
// captions, and drops back to the muted loop when it ends.
function parseVtt(text) {
  const toSec = (t) => { const [h, m, s] = t.trim().split(':'); return Number(h) * 3600 + Number(m) * 60 + Number(s); };
  return text.split(/\n\n+/).map((block) => {
    const lines = block.trim().split('\n');
    const i = lines.findIndex((l) => l.includes('-->'));
    if (i < 0) return null;
    const [a, b] = lines[i].split('-->');
    return { start: toSec(a), end: toSec(b), text: lines.slice(i + 1).join(' ') };
  }).filter(Boolean);
}

function HeroFilm({ narrating, onCutChange, onNarrationEnd, onTime }) {
  const [cut, setCut] = useState(null); // null | 'poster' | 'desktop' | 'mobile'
  const [ready, setReady] = useState(false);
  const [posterReady, setPosterReady] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = Boolean(navigator.connection && navigator.connection.saveData);
    if (reduce || saveData) { setCut('poster'); return undefined; }
    const pick = () => setCut(window.matchMedia('(max-width: 719px)').matches ? 'mobile' : 'desktop');
    if (window.requestIdleCallback) {
      const id = window.requestIdleCallback(pick, { timeout: 1500 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(pick, 250);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => { if (onCutChange) onCutChange(cut); }, [cut, onCutChange]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = true; // React sets the property, not the attribute; be explicit for autoplay policy
    const play = el.play();
    if (play && play.catch) play.catch(() => setCut('poster'));
  }, [cut]);

  // Narration on: restart unmuted, no loop. Off: back to the silent loop.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || cut !== 'desktop') return;
    if (narrating) {
      el.loop = false;
      el.currentTime = 0;
      el.muted = false;
      const p = el.play();
      if (p && p.catch) p.catch(() => onNarrationEnd && onNarrationEnd());
    } else {
      el.muted = true;
      el.loop = true;
      if (el.paused) { const p = el.play(); if (p && p.catch) p.catch(() => {}); }
    }
  }, [narrating, cut, onNarrationEnd]);

  const poster = film('hero-poster.jpg');
  return (
    <div className="fl-land-hero-film" aria-hidden="true">
      {/* Poster always paints underneath; the clip fades in over it once it is actually playing. */}
      <img className={posterReady ? 'is-ready' : ''} src={poster} alt="" decoding="async" fetchpriority="low" onLoad={() => setPosterReady(true)} />
      {(cut === 'desktop' || cut === 'mobile') && (
        <video
          key={cut}
          ref={videoRef}
          className={ready ? 'is-ready' : ''}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onPlaying={() => setReady(true)}
          onError={() => setCut('poster')}
          onEnded={() => onNarrationEnd && onNarrationEnd()}
          onTimeUpdate={(e) => { if (narrating && onTime) onTime(e.currentTarget.currentTime); }}
        >
          {cut === 'desktop' && <source src={film('hero-1080.webm')} type="video/webm" />}
          {cut === 'desktop' && <source src={film('hero-1080.mp4')} type="video/mp4" />}
          {cut === 'mobile' && <source src={film('hero-720-mobile.mp4')} type="video/mp4" />}
        </video>
      )}
      <div className="fl-land-hero-scrim" />
    </div>
  );
}

// Feature spotlights — each pairs copy with an animated product vignette
// (components in components/LandingScenes.js; keyframes in Landing.css).
const SPOTLIGHTS = [
  {
    kicker: 'Deal points register',
    title: 'Negotiate positions, not paragraphs',
    body:
      'Every open point carries each party\u2019s stance, side by side. Lock what\u2019s agreed, see what\u2019s open at a glance, and ask the AI for compromise language grounded in the actual draft — filed as a reviewable edit, never a silent rewrite.',
    scene: SceneNegotiation,
  },
  {
    kicker: 'Audit trail',
    title: 'Every action, attributed',
    body:
      'Versions published, proposals applied, points locked, exports generated — each lands in a per-project audit trail with who, what, and when. AI actions are labeled as AI. When someone asks \u201cwho changed the cap?\u201d, the answer is one scroll away.',
    scene: SceneAudit,
  },
  {
    kicker: 'Versions & storage',
    title: 'Every draft, immutable and exportable',
    body:
      'Drafts never overwrite each other: counterparty markups come in as their own versions (even via account-free drop links), merges and agent edits produce new ones, and any two can be compared with an AI read on what changed for your side. Word exports carry both drafts as real tracked changes.',
    scene: SceneVersions,
  },
  {
    kicker: 'Integrity',
    title: 'Tamper-evident by construction',
    body:
      'Every version is sealed: a SHA-256 hash of its canonical text, chained to the version before it all the way back to genesis, with a sealed copy archived to object-versioned storage. Verification recomputes the chain and reconciles all three records — any altered byte breaks the chain and is caught.',
    scene: SceneHashChain,
  },
];

// What a deal team actually produces — the strip under the hero.
const HERO_STRIP = ['Agreements', 'Financial models', 'Decks & pitchbooks', 'Memos & research', 'IP dockets', 'Federal bids', 'E-signature', 'On-chain proof'];

// "Works with" band. Our own file badges and plain text marks — no vendor
// logos or agency seals (trademark / official-insignia rules), so the tiles
// are drawn here from tokens. Every line describes something the product
// does today; IRS / insurance paper are document types it handles, not
// integrations.
const FILE_TYPES = [
  { ext: '.docx', name: 'Word', tone: 'info', body: 'Counterparty redlines in, true tracked changes out — in their file.' },
  { ext: '.xlsx', name: 'Excel', tone: 'ok', body: 'Models with live formulas, agent edits as reviewable cell changes.' },
  { ext: '.pptx', name: 'PowerPoint', tone: 'warn', body: 'Native 16:9 decks; every slide tagged to its source.' },
  { ext: '.pdf', name: 'PDF', tone: 'danger', body: 'Scans and signed paper in; sealed exports with the certificate out.' },
];

const CONTEXTS = [
  { icon: 'shield', mark: 'USPTO', body: 'Trademark & patent filings, office actions, renewals' },
  { icon: 'folder', mark: 'SAM.gov', body: 'Federal opportunities, fit scoring, bid packages' },
  { icon: 'list', mark: 'Tax & regulatory filings', body: 'Entity forms, statutory notices, agency responses' },
  { icon: 'doc', mark: 'Insurance paper', body: 'Policies, certificates, additional-insured endorsements' },
  { icon: 'key', mark: 'Base', body: 'Version hashes anchored on-chain' },
  { icon: 'chat', mark: 'ChatGPT', body: 'Your workspace as a connector — read, draft, export' },
  { icon: 'pen', mark: 'E-signature', body: 'Built-in ceremonies, no third-party envelope' },
  { icon: 'upload', mark: 'Email & drop links', body: 'Counterparties return markups without an account' },
];

function ArtifactBand() {
  return (
    <section className="fl-land-section fl-art-section" aria-labelledby="fl-art-h">
      <h2 id="fl-art-h">The files deals are actually made of</h2>
      <p className="fl-land-lede">
        Nothing to convert and nothing to re-key. The workspace speaks the formats your counterparties,
        regulators and boards already use — and files, signs and seals in the same place.
      </p>
      <ul className="fl-art-files" aria-label="File formats">
        {FILE_TYPES.map((f) => (
          <li key={f.ext} className={`fl-art-file m-reveal tone-${f.tone}`}>
            <span className="fl-art-badge" aria-hidden="true">
              <span className="fl-art-badge-fold" />
              <span className="fl-art-badge-ext">{f.ext}</span>
            </span>
            <div>
              <strong>{f.name}</strong>
              <p>{f.body}</p>
            </div>
          </li>
        ))}
      </ul>
      <ul className="fl-art-ctx" aria-label="Filings and systems the workspace is built around">
        {CONTEXTS.map((c) => (
          <li key={c.mark} className="m-reveal">
            <Icon name={c.icon} size={16} />
            <span className="fl-art-ctx-mark">{c.mark}</span>
            <span className="fl-art-ctx-body">{c.body}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// The full catalog — every service, grouped by the job it does. Keep each
// line to one true sentence; no numbers that are not in the product.
const CATALOG = [
  {
    icon: 'list',
    name: 'Negotiate',
    lede: 'The working surface for the deal itself.',
    items: [
      ['Deal points register', 'Every open point with each party\u2019s stance side by side; lock what\u2019s agreed, see what\u2019s open.'],
      ['Live redlines', 'Any two versions compared clause by clause, with an AI read on what the change means for your side.'],
      ['Compromise language', 'Ask for middle-ground wording grounded in the actual draft; it files as a reviewable edit.'],
      ['Counterparty intake', 'Their markup arrives through an account-free drop link and lands as its own version — nothing overwritten.'],
      ['Register \u2194 draft conformance', 'A report of where the draft and the agreed positions disagree, before anything goes out.'],
      ['Correspondence reconciliation', 'Paste the email thread; the register updates with what was actually agreed, proposed or withdrawn.'],
      ['Stance-by-party summaries', 'Status pills per item, counterparty / ours / proposed blocks — the internal read after every turn.'],
      ['Memos & cover emails', 'Changes memos, open-items memos and audience-gated email drafts, filed as documents.'],
    ],
  },
  {
    icon: 'pen',
    name: 'Draft & author',
    lede: 'One drafting agent, many kinds of document.',
    items: [
      ['Governed AI drafting', 'Instruct in plain language; every change is a surgical, clause-level proposal you accept or reject.'],
      ['Agreements of every kind', 'MSAs, M&A, credit, operating agreements, term sheets and subscription docs, settlements.'],
      ['Structured documents', 'Functional specs, memos, research reports, policies, playbooks, proposals and RFP responses, filings, manuscripts.'],
      ['Derive for a related entity', 'Turn a HoldCo operating agreement into the OpCo\u2019s: planned, redrafted, with a rationale on every change.'],
      ['Jurisdiction screens', 'Name a jurisdiction; the draft is screened against its statutes and rules, findings filed as edits.'],
      ['Document graph', 'Defined terms, cross-references and similar clauses mapped per document; edits ripple-check their links.'],
      ['Purpose audit & structure map', 'Does each clause earn its place? Is the document shaped the way its kind should be?'],
      ['Word export, both ways', 'Clean .docx or a true tracked-changes redline attributed to the editing party — in the counterparty\u2019s own file.'],
    ],
  },
  {
    icon: 'sheet',
    name: 'Spreadsheets & models',
    lede: 'Excel is a first-class document here.',
    items: [
      ['Workbooks as versions', 'Bring in any .xlsx; every sheet and formula is read, and the model becomes part of the project brain.'],
      ['Model intelligence', 'Input / calc / output roles, the dependency graph and model smells — found deterministically, not guessed.'],
      ['Cell-level agent edits', 'Ask for a change; it lands as versioned cell operations you can review, apply or revert.'],
      ['Workbook audit memo', 'A written audit of a model\u2019s assumptions, structure and risks, filed next to it.'],
      ['Investor-grade authoring', 'House-style financial workbooks built from the project\u2019s facts, exported as real .xlsx.'],
    ],
  },
  {
    icon: 'deck',
    name: 'Presentations',
    lede: 'Decks live with the deal, not in a downloads folder.',
    items: [
      ['Decks as documents', 'Slides with layouts, bullets and speaker notes, versioned like everything else.'],
      ['Agent create & edit', 'Draft a pitchbook from the project\u2019s facts; change a slide the same way you change a clause.'],
      ['PowerPoint export & add-in', 'Native 16:9 .pptx export, and a PowerPoint add-in that keeps slides tagged to their source.'],
      ['Branded exports', 'Board-ready copies on your organization\u2019s letterhead, and certificates of membership interest, shares or awards tied to the sealed agreement.'],
    ],
  },
  {
    icon: 'shield',
    name: 'IP & filings',
    lede: 'Prosecution dockets with the deadlines in view.',
    items: [
      ['Trademark & patent projects', 'USPTO filings, office-action responses and renewals as a project with milestones and next steps.'],
      ['Regulatory filings', 'Filing projects with the same drafting agent, versioning and audit as a negotiated deal.'],
      ['Deadline milestones', 'Dated milestones on every project, with the nearest one always on the dashboard.'],
    ],
  },
  {
    icon: 'search',
    name: 'Government contracting',
    lede: 'From SAM.gov notice to submitted proposal.',
    items: [
      ['SAM.gov pipeline', 'Opportunities synced continuously; fit scoring and bid screens on each one.'],
      ['Deliverability gate', 'An honest read on whether you can actually deliver before anyone writes a page.'],
      ['Bid / watch / pass triage', 'A desk for the pipeline, with the reasoning recorded.'],
      ['Pursue \u2192 proposal project', 'One click creates the proposal project with the notice and attachments already ingested.'],
      ['Compliance matrix', 'Requirements extracted from the solicitation into suggested deal points you can accept.'],
      ['RFI & capability statements', 'Drafted from your entity\u2019s recorded facts, not from a template.'],
    ],
  },
  {
    icon: 'key',
    name: 'Execute',
    lede: 'Sign and share without leaving the record.',
    items: [
      ['E-signature envelopes', 'Signers get an email link and a one-time code — no account. Executed PDFs carry the signatures and a certificate of completion.'],
      ['Pinned to a sealed version', 'Every envelope signs a specific hash-sealed checkpoint; the evidence chain is per envelope.'],
      ['Secure share links', 'Named outsiders read in the browser; a one-time code goes out only if their address is listed; expiry and revoke in-app, every open audited.'],
      ['Certificates', 'Certificates of completion and integrity, with the on-chain transaction in the evidence footer.'],
    ],
  },
  {
    icon: 'check',
    name: 'Prove',
    lede: 'Tamper-evident by construction.',
    items: [
      ['Immutable versions', 'Drafts never overwrite each other; every publish is a new, sealed version.'],
      ['Chained SHA-256 checkpoints', 'Each version\u2019s canonical text is hashed and chained to the one before, back to genesis, with an archived copy in object-versioned storage.'],
      ['Blockchain anchoring', 'Checkpoint and signature hashes are anchored on-chain (Base); the transaction is on every certificate.'],
      ['Verification report', 'Recomputes the chain and reconciles all three records; any altered byte is caught.'],
      ['Audit trail', 'Who, what and when for every version, proposal, lock, export, share and signature. AI actions are labeled as AI.'],
      ['Changes manifest', 'Every publish records exactly which operations produced it, with before / after — and a per-operation revert.'],
    ],
  },
  {
    icon: 'brain',
    name: 'Know',
    lede: 'A system that has actually read the drafts.',
    items: [
      ['Project brain', 'Ask across every document, deal point, analysis and email in a project; answers cite the clause.'],
      ['Agreement ecosystems', 'Related projects reasoned about together — which entity owns which obligation across HoldCo and OpCo.'],
      ['Organization knowledge', 'Your playbooks and positions indexed once and served to chat, voice and the drafting agent, gated by audience.'],
      ['Processing cockpit', 'Watch long AI tasks run: the live knowledge graph, the retrieval map, cost and ETA — and replay the trace afterwards.'],
    ],
  },
  {
    icon: 'chat',
    name: 'Work anywhere',
    lede: 'The whole platform, from wherever you already are.',
    items: [
      ['Inside ChatGPT', 'Connect fast.legal as a ChatGPT app: read, draft, negotiate and export with your own permissions, every call audited.'],
      ['Voice agent', 'Hands-free: ask about a deal, navigate, get suggestions — read-only by design.'],
      ['Word & PowerPoint add-ins', 'Pull and publish versions, map clauses to content controls, step through deal points — inside Office.'],
      ['Command bar', '\u2318K from anywhere: ask, instruct, jump.'],
      ['Exports', 'Word, PowerPoint, Excel and PDF, plus packaged exports with the certificate.'],
      ['API', 'A typed GraphQL layer for aggregations and your own integrations.'],
    ],
  },
  {
    icon: 'users',
    name: 'Govern',
    lede: 'Built for firms, not just for a user.',
    items: [
      ['Organizations & roles', 'Owner, admin and member at the organization; viewer to approver on each project.'],
      ['Guests cost nothing', 'Counterparties, signers and readers never need a seat.'],
      ['Metered honestly', 'AI usage metered per project so you see where it went; one-time packs, never a surprise overage.'],
      ['Guided from day one', 'First-run tours, contextual help, and \u201chow do I\u2026?\u201d answered by the product itself.'],
    ],
  },
];

const ADVANTAGES = [
  {
    them: 'Email + Word redlines',
    theirs: 'Version chaos across inboxes, no shared state of play, redlines that miss the why behind each change.',
    ours: 'One workspace with immutable versions, a live register of every open point, and exports that still speak Word.',
  },
  {
    them: 'Contract repositories / CLM',
    theirs: 'Great at storing signed paper; the actual negotiation still happens somewhere else.',
    ours: 'Built for the negotiation itself — drafting, positions, compromises, compliance screens — storage is the easy part.',
  },
  {
    them: 'Chat-style legal AI',
    theirs: 'Fluent answers with no governance: no proposal flow, no audit trail, no guarantee it read your actual draft.',
    ours: 'Every AI suggestion is labeled, grounded in your documents with citations, and lands as a reviewable edit — never a silent rewrite.',
  },
  {
    them: 'E-signature + a vault + Excel + PowerPoint',
    theirs: 'Four tools, four audit trails, and the model that priced the deal lives nowhere near the agreement it priced.',
    ours: 'Agreement, model, deck and signature in one project, one version history, one hash chain — and the certificate carries all of it.',
  },
];

const FAQS = [
  {
    q: 'Who is fast.legal for?',
    a: 'Teams that negotiate agreements — in-house counsel, firms, and founders running their own deals. If your deals live in email threads and tracked-changes attachments, this replaces that workflow.',
  },
  {
    q: 'Is it only for contracts?',
    a: 'No. A project can be a negotiated deal, an operating-agreement set, a financing, a dispute — or a financial model, a pitchbook, a research report, a policy, an RFP response, a regulatory filing or a trademark docket. The same drafting agent, versioning, audit trail and hash chain apply to all of them, and Excel and PowerPoint files are first-class documents.',
  },
  {
    q: 'What does \u201canchored on-chain\u201d actually mean?',
    a: 'Every version is sealed with a SHA-256 hash chained to the version before it. Checkpoint and signature-chain hashes are then written to the Base blockchain in a transaction the platform controls, and that transaction appears on the certificate. Anyone can recompute the hash and check it against the chain without trusting us.',
  },
  {
    q: 'Does the AI change my documents on its own?',
    a: 'No. All AI output is suggestion-only: edits arrive as proposals you review and apply, suggestions are labeled as such, and every applied change is versioned and audited.',
  },
  {
    q: 'How is my data handled?',
    a: 'Your documents live in your organization\u2019s workspace behind role-based access rules — members see only the projects they\u2019re given. Every version is hash-sealed and archived, and every significant action is recorded in a per-project audit trail.',
  },
  {
    q: 'How does pricing work?',
    a: 'A recurring license per organization (billed monthly, or annually at 20% off) sets your seats, projects, and included AI allowance — Team starts with a 14-day free trial. Heavy months don\u2019t need a plan change: top up with one-time token packs that never expire, metered per project so you can see where usage goes.',
  },
];

const fmtUsd = (cents) => (cents == null ? '—' : `$${Math.round(cents / 100).toLocaleString('en-US')}`);
const fmtTokens = (n) => (n >= 1e6 ? `${n / 1e6}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : String(n));

// Worked examples — put real arithmetic next to the plan grid so buyers can
// place themselves without a sales call.
const EXAMPLES = [
  {
    who: 'Solo counsel',
    detail: '4 deals a month, light agent use',
    math: 'Starter + an occasional 1M pack',
    year: '≈ $1,200–1,500 / year',
    vs: 'about one outside-counsel hour',
  },
  {
    who: '5-lawyer boutique',
    detail: 'counterparty branches, redlines out the door',
    math: 'Team + 0–1 packs most months',
    year: '≈ $4,200 / year',
    vs: 'a fraction of mid-market CLM',
  },
  {
    who: 'In-house, regulated',
    detail: '15 people touching contracts, 2 entities',
    math: 'Enterprise + a 20M pack in a busy quarter',
    year: '≈ $15k–25k / year',
    vs: 'enterprise CLM starts at $20k–40k before AI',
  },
];

export default function Landing() {
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    fetchPlans().then(setCatalog).catch(() => setCatalog(null));
  }, []);

  const plans = useMemo(
    () =>
      Object.entries(catalog?.plans || {})
        .map(([key, p]) => ({ key, ...p }))
        .sort((a, b) => (a.amountCents || 0) - (b.amountCents || 0)),
    [catalog]
  );

  // Narrated playback (desktop cut only). Captions come from the shipped VTT
  // and are rendered here, outside the aria-hidden film layer, so they are
  // announced as they change.
  const [heroCut, setHeroCut] = useState(null);
  const [narrating, setNarrating] = useState(false);
  const [cues, setCues] = useState([]);
  const [cue, setCue] = useState('');
  const canNarrate = heroCut === 'desktop';
  useEffect(() => {
    if (!canNarrate || cues.length) return;
    fetch(film('hero-captions.vtt')).then((r) => (r.ok ? r.text() : '')).then((t) => setCues(parseVtt(t))).catch(() => {});
  }, [canNarrate, cues.length]);
  const stopNarration = useCallback(() => { setNarrating(false); setCue(''); }, []);
  const onNarrationTime = useCallback((t) => {
    const c = cues.find((x) => t >= x.start && t < x.end);
    setCue(c ? c.text : '');
  }, [cues]);

  return (
    <div className="fl-land-root">
    <div className="fl-land">
      <div className="fl-land-top">
        <HeroFilm narrating={narrating} onCutChange={setHeroCut} onNarrationEnd={stopNarration} onTime={onNarrationTime} />
        {/* ---- Top nav ---- */}
        <nav className="fl-land-nav">
          <span className="cs-brand">fast<span>.legal</span></span>
          <div className="fl-land-nav-links">
            <a href="#capabilities">Capabilities</a>
            <a href="#catalog">Everything it does</a>
            <a href="#why">Why fast.legal</a>
            <a href="#pricing">Pricing</a>
          </div>
          <span className="cs-nav-spacer" />
          <Cta to={SIGNIN_URL} className="cs-btn cs-btn-ghost cs-btn-sm">Sign in</Cta>
          <Cta to={SIGNUP_URL} className="cs-btn cs-btn-primary cs-btn-sm">Get started</Cta>
        </nav>

        {/* ---- Hero ---- */}
        {/* Lower-third: the film is the stage, the copy is a compact block
            bottom-left over a bottom-only gradient. The long paragraph and
            the chip strip live in the intro band directly below. */}
        <header className="fl-land-hero">
          {narrating && (
            <p className="fl-land-hero-caption" aria-live="polite">{cue}</p>
          )}
          <div className="fl-land-hero-copy m-rise">
            <div className="fl-land-hero-lead u-stack" style={{ '--gap': 'var(--sp-3)' }}>
              <div className="u-cluster" style={{ '--gap': 'var(--sp-3)' }}>
                <span className="cs-badge">The accountable AI workspace for deal teams</span>
                {canNarrate && (
                  <button
                    type="button"
                    className="cs-btn cs-btn-ghost cs-btn-sm fl-land-hero-sound"
                    aria-pressed={narrating}
                    onClick={() => (narrating ? stopNarration() : setNarrating(true))}
                  >
                    <Icon name={narrating ? 'speakerOff' : 'speaker'} size={14} />
                    {narrating ? 'Mute' : 'Play with sound · 1:04'}
                  </button>
                )}
              </div>
              <h1 className="u-balance">
                Everything the deal needs — <span className="fl-land-accent">drafted</span>,{' '}
                <span className="fl-land-accent">negotiated</span>, <span className="fl-land-accent">proven</span>.
              </h1>
            </div>
            <div className="fl-land-hero-aside u-stack" style={{ '--gap': 'var(--sp-4)' }}>
              <p>One AI workspace that drafts, negotiates, signs and seals every version on-chain.</p>
              <div className="u-cluster" style={{ '--gap': 'var(--sp-3)' }}>
                <Cta to={SIGNUP_URL} className="cs-btn cs-btn-primary">Create your workspace</Cta>
                <a className="cs-btn cs-btn-outline" href="#catalog">See everything it does</a>
              </div>
            </div>
          </div>
        </header>
      </div>

      {/* ---- Intro band: the hero's caption ---- */}
      <section className="fl-land-section fl-land-intro">
        <p className="fl-land-intro-lede">
          Agreements, financial models, decks, memos, IP dockets and federal bids — one AI
          workspace that drafts in reviewable edits, negotiates by the point, signs, and seals
          every version with a hash anchored on-chain. Built for counsel, finance and the teams
          around them.
        </p>
        <p className="fl-land-hero-note">
          Every new workspace starts with a 14-day Team trial — no card required. Plans from $99/month.
        </p>
        <ul className="fl-land-hero-strip" aria-label="What fast.legal produces">
          {HERO_STRIP.map((t) => <li key={t}>{t}</li>)}
        </ul>
      </section>

      {/* ---- Formats + filings the workspace speaks ---- */}
      <ArtifactBand />

      {/* ---- Contract lifecycle band ---- */}
      <section className="fl-land-section fl-land-lc-section">
        <h2>The whole lifecycle, one record</h2>
        <p className="fl-land-lede">
          Legal terms and business points move together — from the first AI draft, through the
          model and the deck, to a signed, sealed, audit-ready archive.
        </p>
        <LifecycleBand />
      </section>

      {/* ---- Feature spotlights ---- */}
      <section id="capabilities" className="fl-land-section">
        <h2>Built for how deals actually move</h2>
        <p className="fl-land-lede">
          Not a document vault, not a chatbot — the working surface for the deal itself, and for
          everything the deal team produces around it.
        </p>
        <div className="fl-spots">
          {SPOTLIGHTS.map((s) => (
            <div key={s.title} className="fl-spot m-reveal">
              <div className="fl-spot-copy">
                <span className="fl-spot-kicker">{s.kicker}</span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
              <div className="fl-spot-visual">
                <s.scene />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- The full catalog ---- */}
      <section id="catalog" className="fl-land-section">
        <h2>Everything it does</h2>
        <p className="fl-land-lede">
          Not a feature list from a slide — the catalog of what the platform actually ships, grouped
          by the job it does. Every line is live in the product today.
        </p>
        <nav className="fl-cat-jump" aria-label="Catalog sections">
          {CATALOG.map((g) => <a key={g.name} href={`#cat-${g.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{g.name}</a>)}
        </nav>
        <div className="fl-cat">
          {CATALOG.map((g, i) => (
            <section key={g.name} id={`cat-${g.name.toLowerCase().replace(/[^a-z]+/g, '-')}`} className="fl-cat-group m-reveal" style={{ '--m-delay': `${(i % 3) * 60}ms` }}>
              <header className="fl-cat-head">
                <span className="fl-land-cap-icon"><Icon name={g.icon} size={20} /></span>
                <div>
                  <h3>{g.name}</h3>
                  <p>{g.lede}</p>
                </div>
              </header>
              <ul className="fl-cat-items">
                {g.items.map(([name, body]) => (
                  <li key={name}>
                    <strong>{name}</strong>
                    <span>{body}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </section>

      {/* ---- Why fast.legal ---- */}
      <section id="why" className="fl-land-section">
        <h2>Why teams switch</h2>
        <p className="fl-land-lede">
          The honest comparison — what you’re probably using today, and what changes.
        </p>
        <div className="fl-land-vs-grid">
          {ADVANTAGES.map((a) => (
            <div key={a.them} className="cs-card fl-land-vs">
              <h3>{a.them}</h3>
              <p className="fl-land-vs-theirs">{a.theirs}</p>
              <p className="fl-land-vs-ours"><Icon name="arrowRight" size={14} /> {a.ours}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Pricing ---- */}
      <section id="pricing" className="fl-land-section">
        <h2>Simple pricing, metered honestly</h2>
        <p className="fl-land-lede">
          A recurring license covers your seats, projects, and monthly AI allowance — billed
          monthly or annually (20% off), cancel anytime. Busy months are covered by one-time
          token packs, never a surprise overage bill. Counterparties on guest links are always
          free, and usage is metered per project so you know exactly where it went.
        </p>
        {plans.length > 0 ? (
          <div className="fl-land-plans">
            {plans.map((p) => {
              const featured = p.key === 'team';
              const cta = `${APP_BASE}/login?signup=1&plan=${p.key}&next=${encodeURIComponent(`/pricing?plan=${p.key}`)}`;
              return (
                <div key={p.key} className={`cs-card fl-land-plan${featured ? ' featured' : ''}`}>
                  {featured && <span className="fl-land-plan-flag">Most firms</span>}
                  <h3>{p.name}</h3>
                  {p.tagline && <span className="fl-land-plan-tagline">{p.tagline}</span>}
                  <div className="fl-land-plan-price">
                    {p.fromPrice && <span className="fl-land-plan-from">from </span>}
                    {fmtUsd(p.amountCents)}<span> / month</span>
                  </div>
                  <span className="fl-land-plan-note">
                    Recurring · cancel anytime{p.amountCentsAnnual ? ` · ${fmtUsd(p.amountCentsAnnual)}/mo billed annually` : ''}
                  </span>
                  {p.trialDays > 0 && <span className="fl-land-plan-trial">{p.trialDays}-day free trial</span>}
                  <ul>
                    {(p.features || []).slice(0, 5).map((f) => <li key={f}>{f}</li>)}
                  </ul>
                  <Cta to={cta} className={`cs-btn ${featured ? 'cs-btn-primary' : 'cs-btn-outline'}`}>
                    {p.trialDays > 0 ? `Try ${p.name} free` : `Start with ${p.name}`}
                  </Cta>
                  {p.fromPrice && (
                    <a className="fl-land-plan-talk" href="mailto:sales@fast.legal?subject=fast.legal%20Enterprise">
                      Custom above — talk to us
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="fl-land-plans">
            {['Starter', 'Team', 'Enterprise'].map((name) => (
              <div key={name} className="cs-card fl-land-plan">
                <h3>{name}</h3>
                <div className="cs-skeleton" style={{ height: 120 }} />
              </div>
            ))}
          </div>
        )}
        {catalog?.packs && (
          <p className="fl-land-packs-note">
            On-demand usage: <strong>one-time</strong> token packs from {fmtUsd(Object.values(catalog.packs).sort((a, b) => a.amountCents - b.amountCents)[0]?.amountCents)}
            {' '}({fmtTokens(Object.values(catalog.packs).sort((a, b) => a.amountCents - b.amountCents)[0]?.tokens || 0)} tokens) —
            not subscriptions. Credits never expire and draw down against actual usage.
          </p>
        )}
        <div className="fl-land-examples">
          {EXAMPLES.map((e) => (
            <div key={e.who} className="fl-land-example">
              <strong>{e.who}</strong>
              <span className="fl-land-example-detail">{e.detail}</span>
              <span className="fl-land-example-math">{e.math}</span>
              <span className="fl-land-example-year">{e.year}</span>
              <span className="fl-land-example-vs">{e.vs}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ---- FAQ ---- */}
      <section className="fl-land-section fl-land-faq">
        <h2>Questions, answered plainly</h2>
        <div className="fl-land-faq-grid">
          {FAQS.map((f) => (
            <div key={f.q} className="fl-land-faq-item">
              <h3>{f.q}</h3>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Final CTA ---- */}
      <section className="fl-land-cta cs-card m-reveal">
        <h2 className="u-balance">Run your next deal on fast.legal</h2>
        <p>Create a workspace, bring in a draft, a model or a deck, and watch the register, the agent and the hash chain go to work in minutes.</p>
        <div className="u-cluster" style={{ '--gap': 'var(--sp-3)', justifyContent: 'center' }}>
          <Cta to={SIGNUP_URL} className="cs-btn cs-btn-primary">Create your workspace</Cta>
          <Cta to={SIGNIN_URL} className="cs-btn cs-btn-outline">Sign in</Cta>
        </div>
      </section>

      <footer className="fl-land-foot">
        <span>© {new Date().getFullYear()} fast.legal</span>
        <div className="u-cluster" style={{ '--gap': 'var(--sp-4)' }}>
          <a href="#capabilities">Capabilities</a>
          <a href="#catalog">Everything it does</a>
          <a href="#pricing">Pricing</a>
          <a href="/connect">ChatGPT app</a>
          <Cta to={SIGNIN_URL} className="">Sign in</Cta>
        </div>
      </footer>
    </div>
    </div>
  );
}
